/**
 * useWorkflowIntegrationGate — the REQUIRED-integration hard gate for a
 * DEPLOYED workflow (the Trigger surfaces).
 *
 * Triggering an agent whose required integration (e.g. GitHub for
 * github-code-review) is not connected produces a guaranteed-useless run that
 * still bills — so Trigger must be disabled, with a hint naming exactly what
 * to connect. This hook reads the SAME source the SKILLS section
 * (IntegrationsConnectList) renders from — GET
 * /workflows/{uuid}/integrations/status — and derives the gate:
 *
 *   { status, missingLabels, blocked, refetch }
 *
 * - `missingLabels` — display names of the required-but-unconnected
 *   integrations ("GitHub", "GitHub or GitLab" for an OR-group).
 * - `blocked` — true iff at least one required integration is unconnected.
 *
 * Fail-open by design: while loading, or when the status fetch errors, the
 * gate does NOT block (blocked=false) — we only gate on POSITIVE knowledge
 * that something required is missing. Optional integrations never block.
 *
 * React-Query cached (short staleTime, same spirit as useAgent) so the
 * detail-page header and the trigger modal share one fetch.
 */

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import api from '../services/api';
import { missingRequiredLabels } from '../utils/enabledIntegrations';

export default function useWorkflowIntegrationGate(workflowUuid) {
  const { data: status = null, refetch } = useQuery({
    queryKey: ['workflow-integration-status', workflowUuid],
    queryFn: () => api.get(`/workflows/${encodeURIComponent(workflowUuid)}/integrations/status`),
    enabled: !!workflowUuid,
    staleTime: 10 * 1000,
  });

  const missingLabels = useMemo(() => missingRequiredLabels(status), [status]);

  return {
    status,
    missingLabels,
    blocked: missingLabels.length > 0,
    refetch,
  };
}
